'use client'

import { useIsActiveHideVatPrice } from '@/feature/product/hooks/useIsActiveHideVatPrice'
import { twMerge } from '@/lib/classname/twMerge'
import { formatPrice } from '@/lib/formatters/currency.formatter'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { Unit } from '@gql-generated/graphql'
import type React from 'react'
import type { ProductTierPriceProps } from './ProductTiersPrices.client'

type ProductTiersPricesGridProps = {
  tiersPrices: ProductTierPriceProps[]
  quantity: number
  unit: Unit
}

export function ProductTiersPricesGrid({
  tiersPrices,
  quantity,
  unit,
}: ProductTiersPricesGridProps): React.ReactElement | null {
  const { t, Trans } = useTranslation(['productTiersPrices', 'generic'])
  const isActiveHideVatPrice = useIsActiveHideVatPrice()

  if (!tiersPrices?.length) return null

  const unitLabel = t(`generic:units.${unit || Unit.P}`, { count: quantity })

  const validTiers = tiersPrices.filter(
    (tier): tier is { quantity: number; exclTax: number } =>
      typeof tier.quantity === 'number' && typeof tier.exclTax === 'number' && tier.exclTax > 0,
  )

  return (
    <div className="flex w-full flex-col font-medium">
      {validTiers.map((tier, idx) => {
        const isOdd = idx % 2 === 0
        const cellClasses = twMerge(
          isOdd && 'bg-brand-2-50',
          'flex flex-1 items-center gap-0.5 rounded-xs px-2 py-1 text-center',
        )

        return (
          <div key={tier.quantity} className="flex w-full gap-0.5">
            <div className={cellClasses}>
              <strong>{tier.quantity}</strong>
              <span>{t('productTiersPrices:andMore')}</span>
            </div>

            <div className={twMerge(cellClasses, 'min-w-fit')}>
              <Trans
                i18nKey="productTiersPrices:priceAndUnits"
                values={{
                  price: isActiveHideVatPrice
                    ? formatPrice(tier.exclTax)
                    : t('productTiersPrices:priceExclTax', { value: tier.exclTax }),
                  nbUnit: quantity,
                  textUnit: unitLabel,
                }}
                components={{
                  price: <span className="font-bold text-strawberry-red-600" />,
                  units: <span />,
                }}
              />
            </div>
          </div>
        )
      })}
    </div>
  )
}
